import { mergePopularidadEncabezado } from './popularidadReportModel';
import { formatPeriodoDescuentosLine } from './descuentosCortesiasReportModel';

export const CANCELACIONES_REPORT_TITULO_DEFAULT = 'CANCELACIONES';

function foldKeyLoose(k) {
  return String(k)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\s-]/g, '')
    .toLowerCase();
}

function keyMatches(spec, rawKey) {
  return spec.patterns.some((re) => re.test(rawKey) || re.test(foldKeyLoose(rawKey)));
}

/**
 * Columnas del impreso cancelaciones (mockup); la primera fila enlaza claves del API.
 */
export const CANCELACIONES_COLUMN_SPECS = [
  {
    id: 'folio',
    label: 'Folio',
    patterns: [/^folio$/i, /^ticket$/i, /^cuenta$/i, /^nocuenta$/i, /^noCuenta$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'cantidad',
    label: 'Cantidad',
    patterns: [/^cantidad$/i, /^qty$/i, /^cant\.?$/i],
    align: 'right',
    total: true,
    kind: 'qty',
  },
  {
    id: 'nombre',
    label: 'Nombre',
    patterns: [
      /^nombre$/i,
      /^descripcion$/i,
      /^descripción$/i,
      /^producto$/i,
      /^nombreproducto$/i,
      /^nombreProducto$/i,
      /^articulo$/i,
      /^artículo$/i,
    ],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'precio',
    label: 'Precio',
    patterns: [
      /^precio$/i,
      /^preciounitario$/i,
      /^precioUnit$/i,
      /^price$/i,
    ],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'autoriza',
    label: 'Autorizo',
    patterns: [
      /^autorizo$/i,
      /^autorizó$/i,
      /^autoriza$/i,
      /^autorizado$/i,
      /^quienautoriza$/i,
      /^usuarioautoriza$/i,
    ],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'motivo',
    label: 'Comentario',
    patterns: [
      /^comentario$/i,
      /^motivo$/i,
      /^observacion$/i,
      /^observación$/i,
      /^razon$/i,
      /^razón$/i,
    ],
    align: 'left',
    kind: 'text',
  },
];

/** @param {Record<string, unknown>|null|undefined} sampleRow */
export function resolveCancelacionesColumns(sampleRow) {
  const keys =
    sampleRow && typeof sampleRow === 'object' && !Array.isArray(sampleRow)
      ? Object.keys(sampleRow)
      : [];
  const used = new Set();
  return CANCELACIONES_COLUMN_SPECS.map((spec) => {
    const k = keys.find((kk) => !used.has(kk) && keyMatches(spec, kk));
    if (k) used.add(k);
    return { ...spec, sourceKey: k ?? null };
  });
}

/** @param {Record<string, unknown>} raw @param {ReturnType<typeof resolveCancelacionesColumns>} resolved */
export function rowToCancelacionesNorm(raw, resolved) {
  const o = {};
  for (const c of resolved) {
    o[c.id] = c.sourceKey ? raw[c.sourceKey] : undefined;
  }
  return o;
}

function fmtUnDecimal(n) {
  return n.toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** @param {Record<string, unknown>[]} normRows @param {ReturnType<typeof resolveCancelacionesColumns>} resolved */
export function sumCancelacionesTotals(normRows, resolved) {
  const totals = {};
  const cantidadSpec = resolved.find((c) => c.id === 'cantidad');
  for (const c of resolved) {
    if (!c.total) continue;
    let s = 0;
    for (const r of normRows) {
      if (c.id === 'precio') {
        const qty = cantidadSpec ? Number(r[cantidadSpec.id]) : 0;
        const prc = Number(r[c.id]);
        if (!Number.isNaN(qty) && !Number.isNaN(prc)) s += qty * prc;
      } else {
        const n = Number(r[c.id]);
        if (!Number.isNaN(n)) s += n;
      }
    }
    totals[c.id] = s;
  }
  return totals;
}

const nFinite = (v) => {
  if (v == null || v === '') return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

/**
 * Totales del API → ids de columnas normalizadas.
 * @param {Record<string, unknown>|null|undefined} apiTotals
 */
export function mapApiCancelacionesTotalesToNormIds(apiTotals) {
  if (!apiTotals || typeof apiTotals !== 'object') return null;
  const out = {};
  const set = (id, ...candidates) => {
    for (const raw of candidates) {
      const x = nFinite(raw);
      if (x != null) {
        out[id] = x;
        return;
      }
    }
  };
  set('cantidad', apiTotals.cantidad, apiTotals.piezas, apiTotals.totalCantidad, apiTotals.sumCantidad);
  set('precio', apiTotals.importe, apiTotals.totalImporte, apiTotals.monto, apiTotals.total);
  set('importe', apiTotals.importe, apiTotals.totalImporte, apiTotals.monto, apiTotals.total);
  return Object.keys(out).length ? out : null;
}

export function mergeCancelacionesFootTotals(summed, fromApi, resolved) {
  const base = { ...summed };
  if (!fromApi || typeof fromApi !== 'object') return base;
  for (const c of resolved) {
    if (!c.total) continue;
    if (fromApi[c.id] != null && !Number.isNaN(Number(fromApi[c.id]))) {
      base[c.id] = Number(fromApi[c.id]);
    }
  }
  return base;
}

/** @param {ReturnType<typeof resolveCancelacionesColumns>} resolved @param {Record<string, string>|null|undefined} columnas */
export function applyCancelacionesColumnLabelsFromApi(resolved, columnas) {
  if (!columnas || typeof columnas !== 'object' || Array.isArray(columnas)) return resolved;
  return resolved.map((c) => {
    if (!c.sourceKey) return c;
    const label = columnas[c.sourceKey];
    if (typeof label === 'string' && label.trim()) return { ...c, label: label.trim() };
    return c;
  });
}

export function formatCancelacionesCell(spec, value) {
  if (value == null || value === '') return '—';
  if (spec.kind === 'qty') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : fmtUnDecimal(n);
  }
  if (spec.kind === 'money') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : fmtUnDecimal(n);
  }
  return String(value);
}

export function formatCancelacionesTotalCell(spec, value) {
  if (!spec.total) return '—';
  if (value == null || Number.isNaN(Number(value))) return '—';
  return formatCancelacionesCell({ ...spec, kind: spec.id === 'cantidad' ? 'qty' : 'money' }, value);
}

export function mergeCancelacionesEncabezado(apiEnc, encabezadoFallback) {
  return mergePopularidadEncabezado(apiEnc, encabezadoFallback, {
    reporteTitulo: CANCELACIONES_REPORT_TITULO_DEFAULT,
    incluirCortesias: false,
    lineaCortesias: '',
    cortesiasSubtitle: '',
  });
}

export function withCancelacionesPeriodoLine(enc, fechaInicio, fechaFin) {
  const e = enc && typeof enc === 'object' ? { ...enc } : {};
  if (!String(e.periodoTexto || '').trim() && (fechaInicio || fechaFin)) {
    e.periodoTexto = formatPeriodoDescuentosLine(fechaInicio, fechaFin);
  }
  return e;
}
